import axios, { AxiosError, type AxiosInstance, type AxiosResponse } from 'axios';
import { loginRequest } from '@/authConfig';
import { publicRoutes } from '@/constants';
import { msalInstance } from '@/msal';

function isPublicRoute(routePatterns: string[]) {
    // Check if the current location matches any of the specified route patterns using useLocation hook
    return routePatterns.some((pattern) => location.pathname.startsWith(pattern));
}

// Create an instance
const axiosInstance: AxiosInstance = axios.create({
    baseURL: import.meta.env.VITE_APP_API_URL as string,
    timeout: 30_000,
});

// Add a request interceptor
axiosInstance.interceptors.request.use(
    async (config) => {
        // Skip authentication for public routes - these endpoints don't require auth
        // and calling acquireTokenSilent() can fail on some Android browsers
        if (isPublicRoute(publicRoutes)) {
            return config;
        }

        try {
            const authRes = await msalInstance.acquireTokenSilent({
                scopes: loginRequest.scopes,
            });
            config.headers.Authorization = `Bearer ${authRes.accessToken}`;
        } catch (error) {
            console.log('Auth res error:', error);
            msalInstance?.loginRedirect(loginRequest).catch((error) => console.log(error));
            throw new AxiosError('Authentication required', 'ERR_AUTH_REQUIRED', config);
        }
        return config;
    },
    (error: AxiosError): Promise<AxiosError> => {
        return Promise.reject(error);
    },
);

// Add a response interceptor
axiosInstance.interceptors.response.use(
    (response: AxiosResponse): AxiosResponse | Promise<AxiosResponse> => {
        return response;
    },
    (error: AxiosError): Promise<AxiosError> => {
        // handle the response error
        if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            console.error(error.response);
        } else if (error.request) {
            // The request was made but no response was received
            // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
            // http.ClientRequest in Node.js
            console.error(error.request);
        } else {
            // Something happened in setting up the request that triggered an Error
            console.error('Error', error.message);
        }
        return Promise.reject(error);
    },
);

export default axiosInstance;

export async function getAccessToken(): Promise<string | null> {
    try {
        const authRes = await msalInstance.acquireTokenSilent({
            scopes: loginRequest.scopes,
        });
        return authRes.accessToken;
    } catch (error) {
        console.error('Error getting access token:', error);
        return null;
    }
}
